import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render-time crash and shows what went wrong.
 *
 * Without this, React unmounts the whole tree on an uncaught error and you get a blank white page —
 * no app shell, no message, nothing to act on. That happened on a change-set page and cost an
 * afternoon of guessing. A crash should always leave you with the error, a way to retry, and a way out.
 */
interface Props {
  children: ReactNode;
  /** Shown above the message, e.g. the page that failed. */
  where?: string;
}
interface State {
  error: Error | null;
  info: string | null;
  /** One silent remount has already been tried for this boundary — a second crash is shown. */
  recovered: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, recovered: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the stack in the console too — the visible message is a summary, this is the detail.
    console.error("UI crash:", error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });

    // "insertBefore"/"removeChild" mean React's tree and the real DOM drifted apart — usually because
    // something outside React (a browser extension, a translator) edited the page. A fresh mount rebuilds
    // the DOM from scratch and almost always fixes it, so try that ONCE silently before showing an error
    // screen for what is, to the user, a blip. A second crash means it is structural: show it.
    const domDesync = error.name === "NotFoundError" || /insertBefore|removeChild|not a child of this node/i.test(error.message);
    if (domDesync && !this.state.recovered) {
      this.setState({ error: null, info: null, recovered: true });
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <h1 className="text-base font-semibold text-red-800">Something broke while drawing this page{this.props.where ? ` (${this.props.where})` : ""}</h1>
          <p className="mt-1 text-sm text-red-900">{error.message || String(error)}</p>
          <p className="mt-2 text-xs text-red-700">
            Your data is safe — this is the screen, not the sync. Reloading usually clears it; if it comes back, this message is what to report.
            {this.state.recovered && " This page was already rebuilt once automatically and crashed again, so something is genuinely editing the page — a browser extension is the usual culprit; try an incognito window."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => window.location.reload()} className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">
              Reload the page
            </button>
            <a href="/reconcile" className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50">
              Back to change sets
            </a>
            <button
              onClick={() => void navigator.clipboard?.writeText(`${error.message}\n\n${error.stack ?? ""}\n\n${info ?? ""}`)}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-50"
            >
              Copy details
            </button>
          </div>
          {info && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-medium text-red-800">Technical details</summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-white/70 p-2 text-[11px] leading-snug text-red-900">
                {error.stack ?? error.message}
                {info}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
